import React, { useCallback } from 'react';
import _ from 'lodash';

import AnomalyDescription from '../../custom-visual-inspection/report-items/anomalyDescription';
import AnomalyLocation from '../../custom-visual-inspection/anomaly-location';
import ConcreteHammerPDF from '../concrete-hammer-template/report-pdf';
import Conditional from '../../../common/conditional';
import CustomUTMReportPDF from '../../custom-utm/custom-utm-report';
import CustomUTMAnomaly from '../../custom-utm/customUTMAnomaly';
import CustomSection from '../../custom-general/custom-section';
import CustomGeneralPhotos from '../../custom-general/custom-general-photos';
import LPTReportSummary from '../../custom-lpt/lpt-report';
import MPIReportSummary from '../../custom-mpi/mpi-report';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';
import PCNLRiskAssessmentTable from '../pcnl-risk-assessment/table';

import { chunkRiskAssessmentPerPage, formatNumberRange, getChunkedData, getItemsChunkedData, matchPhrases } from '../../../../utils/utilities';
import { PCNLItemModel } from '../../../../utils/models';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../../utils/constants';
import { getReportDetails } from '../../../../store/reportDetails';
import { useAppSelector } from '../../../../store/configureStore';

interface Props {
	pcnlItems: PCNLItemModel[];
}

const PCNLItemReport: React.FC<Props> = ({ pcnlItems }) => {
	const { itemCountPerPage } = useAppSelector(getReportDetails);

	const getChunkedAnomalies = useCallback((pcnlItem: PCNLItemModel) => {
		const inspectedItemWithoutAnomalies = _.omit(pcnlItem, ['anomalyItems']);

		return getChunkedData(pcnlItem.anomalyItems, TABLE_ITEM_COUNT_LIMITS.EFVIS).map((chunkedAnomalies) => ({ ...inspectedItemWithoutAnomalies, anomalyItems: chunkedAnomalies }))
	}, []);

    const getTitleForReportPage = useCallback((title: string, pcnlItem: PCNLItemModel) => {
        return `${title} - ${pcnlItem.pcnlItemName}`
    }, []);

	const renderAnomalyTablePage = useCallback((pcnlItem: PCNLItemModel) => {
        const chunkedAnomalies = getChunkedAnomalies(pcnlItem).length > 0 ? getChunkedAnomalies(pcnlItem) : [pcnlItem];

        return (
            <React.Fragment>
                {chunkedAnomalies.map((chunk, index) => (
                    <CustomSection key={index} sectionTitle="Inspection Findings" sectionSubtitle={getTitleForReportPage("Anomaly Summary", pcnlItem)}>
                        <div className="p-1">
                            <PDFTablePage columns={columns} data={[chunk]} />
                        </div>
                    </CustomSection>
                ))}
            </React.Fragment>
        );
	}, [getChunkedAnomalies, getTitleForReportPage]);

    const renderRiskAssessmentTablePage = useCallback((pcnlItem: PCNLItemModel) => {
            return  (
                <React.Fragment>
                    {chunkRiskAssessmentPerPage(pcnlItem).map((chunk, index) => (
                        <CustomSection key={index} sectionTitle="Inspection Findings" sectionSubtitle={getTitleForReportPage("Risk Assessment", pcnlItem)}>
                            <PCNLRiskAssessmentTable data={chunk} />
                        </CustomSection>
                    ))}
                </React.Fragment>
            );
    }, [getTitleForReportPage]);

    const getMPTChunkedData = useCallback((pcnlItem: PCNLItemModel) => {
        return getItemsChunkedData(pcnlItem.mptData.data, TABLE_ITEM_COUNT_LIMITS.MPI, itemCountPerPage);
    }, [itemCountPerPage]);
    
    const getLPTChunkedData = useCallback((pcnlItem: PCNLItemModel) => {
        return getItemsChunkedData(pcnlItem.lptData.data, TABLE_ITEM_COUNT_LIMITS.LPT, itemCountPerPage);
    }, [itemCountPerPage]);

    const getAnomalyHeaders = useCallback((item: PCNLItemModel) => {
        return [
            {
                label: 'PCNL Item Name',
                value: item.pcnlItemName,
            },
        ];
    }, []);


	return (
		<>
			{pcnlItems.map((pcnlItem) => (
                <React.Fragment key={pcnlItem.pcnlItemName}>
                    <Conditional isVisible={!matchPhrases(pcnlItem.pcnlItemName, 'risk assessment')}>
                        {renderAnomalyTablePage(pcnlItem)}
                    </Conditional>

                    <Conditional isVisible={matchPhrases(pcnlItem.pcnlItemName, 'risk assessment')}>
                        {renderRiskAssessmentTablePage(pcnlItem)}
                    </Conditional>

                    <CustomUTMAnomaly
                        lineNumberID={pcnlItem.id}
                        anomalyItems={pcnlItem.anomalyItems as any}
                        headers={getAnomalyHeaders(pcnlItem)}
                    />

                    <Conditional isVisible={pcnlItem.anomalyLocations.length > 0}>
                        <AnomalyLocation locationImages={pcnlItem.anomalyLocations} />
                    </Conditional>

                    <Conditional isVisible={pcnlItem.generalPhotos.length > 0}>
						<CustomGeneralPhotos identifier={pcnlItem.pcnlItemName} label="Inspection Findings" subLabel={getTitleForReportPage("Photographic Record", pcnlItem)} photos={pcnlItem.generalPhotos} />
					</Conditional>

                    <Conditional isVisible={matchPhrases(pcnlItem.hasLPTData, 'yes')}>
                        <CustomSection sectionTitle="Inspection Findings" sectionSubtitle={getTitleForReportPage('Liquid Penetrant Test Report', pcnlItem)}>
                            <Conditional isVisible={pcnlItem.lptData.data.length > 0}>
                                {getLPTChunkedData(pcnlItem).map((chunk, index) => (
                                    <LPTReportSummary 
                                        {...pcnlItem.lptData} 
                                        data={chunk} 
                                        showOverview={index === 0} 
                                        showSummary={index === (getLPTChunkedData(pcnlItem).length - 1)} 
                                    />
                                ))}
                            </Conditional>

                            <Conditional isVisible={pcnlItem.lptData.data.length === 0}>
                                <LPTReportSummary {...pcnlItem.lptData} showOverview showSummary />
                            </Conditional>
                        </CustomSection>
                    </Conditional>
                    
                    <Conditional isVisible={matchPhrases(pcnlItem.hasMPTData, 'yes')}>
                        <CustomSection sectionTitle="Inspection Findings" sectionSubtitle={getTitleForReportPage('Magnetic Particle Test Report', pcnlItem)}>
                            <Conditional isVisible={pcnlItem.mptData.data.length > 0}>
                                {getMPTChunkedData(pcnlItem).map((chunk, index) => (
                                    <MPIReportSummary 
                                        {...pcnlItem.mptData} 
                                        data={chunk} 
                                        showOverview={index === 0} 
                                        showSummary={index === (getMPTChunkedData(pcnlItem).length - 1)} 
                                    />
                                ))}
                            </Conditional>
                            
                            <Conditional isVisible={pcnlItem.mptData.data.length === 0}>
                                <MPIReportSummary 
                                    {...pcnlItem.mptData}
                                    showOverview
                                    showSummary
                                />
                            </Conditional>
                        </CustomSection>
                    </Conditional>

                    <Conditional isVisible={matchPhrases(pcnlItem.hasUTMData, 'yes')}>
                        <CustomUTMReportPDF
                         utInspectionItems={pcnlItem.utmData.items}
                         inspectionOverview={pcnlItem.utmData.overview as any}
                         tmlLocations={[]}
                        />
                    </Conditional>

                    <ConcreteHammerPDF 
                        isVisible={matchPhrases(pcnlItem.hasConcreteData, 'yes')} 
                        item={pcnlItem.concreteHammerData} 
                    />
                </React.Fragment>
            ))}
		</>
	);
};

const columns = [
	{
		path: 'id',
		label: 'Sr No.',
	},
	{
		path: 'pcnlItemName',
		label: 'PCNL Item Name',
	},
	{
		path: 'inspectionDescription',
		label: 'Anomaly / Inspection Description',
		content: (item: PCNLItemModel) =>
			<div>
                {item.anomalyItems.length > 0 ? item.anomalyItems.map((anomaly) => <AnomalyDescription key={anomaly.id} anomaly={anomaly} />) : 'N/A'}
			</div>
	},
	{
		path: 'images',
		label: 'Anomaly / Photo Ref.',
		content: (item: PCNLItemModel) => <span>{item.anomalyItems.length > 0 ? formatNumberRange(item.anomalyItems.map((anomaly) => anomaly.id)) : 'N/A'}</span>
	},
];

export default PCNLItemReport;
